import React, { useState } from "react";
import { Link } from "react-router-dom";

const CONTENT = {
  privacy: {
    en: {
      title: "Privacy Policy",
      updated: "Last updated: February 2026",
      body: [
        ["Overview", "LoadWise ('we', 'us') provides SaaS profitability tools for US dispatchers and truck drivers. We collect only the data required to operate the service and never sell personal information."],
        ["Data we collect", "Account details (email, name), billing metadata via Lemon Squeezy (we never see your card), load data you save, feedback you submit, and standard technical logs (IP, user agent, timestamps)."],
        ["How we use data", "To run your account, compute load profitability, process payments through Lemon Squeezy, send transactional emails, prevent abuse, and improve the product."],
        ["Third parties", "Lemon Squeezy (payments), Resend (email), EIA (public fuel prices), Anthropic via Emergent (AI advisor). Each provider has its own privacy policy."],
        ["Retention", "Account and load data are retained while your account is active and up to 12 months after cancellation. You may delete your account any time by contacting support."],
        ["Your rights", "Access, correction, deletion, portability, and opt-out are available on request via support@loadwise.app."],
        ["Contact", "Questions: support@loadwise.app"],
      ],
    },
    ar: {
      title: "سياسة الخصوصية",
      updated: "آخر تحديث: فبراير 2026",
      body: [
        ["نظرة عامة", "توفر منصة LoadWise أدوات SaaS لحساب ربحية الحمولات للديسباتشرز والسائقين في الولايات المتحدة. نجمع فقط البيانات اللازمة لتشغيل الخدمة ولا نبيع المعلومات الشخصية أبدًا."],
        ["البيانات التي نجمعها", "تفاصيل الحساب (البريد، الاسم)، بيانات الدفع عبر Lemon Squeezy (لا نرى بطاقتك أبدًا)، بيانات الحمولات التي تحفظها، الـ feedback الذي تقدمه، وسجلات تقنية قياسية."],
        ["كيف نستخدم البيانات", "لتشغيل حسابك وحساب الربحية ومعالجة المدفوعات عبر Lemon Squeezy وإرسال رسائل بريد إلكتروني ومنع الإساءة وتحسين المنتج."],
        ["أطراف ثالثة", "Lemon Squeezy (المدفوعات)، Resend (البريد)، EIA (أسعار الوقود)، Anthropic عبر Emergent (المستشار الذكي). لكل مزود سياسة خصوصيته الخاصة."],
        ["الاحتفاظ", "يتم الاحتفاظ ببيانات الحساب حتى 12 شهرًا بعد الإلغاء. يمكنك حذف حسابك في أي وقت عبر الدعم."],
        ["حقوقك", "الوصول، التصحيح، الحذف، النقل، والانسحاب متاحة عبر support@loadwise.app."],
        ["اتصل بنا", "استفسارات: support@loadwise.app"],
      ],
    },
  },
  terms: {
    en: {
      title: "Terms of Service",
      updated: "Last updated: February 2026",
      body: [
        ["Acceptance", "By using LoadWise you agree to these terms. If you don't agree, don't use the service."],
        ["Service", "We provide a profitability calculator, load history, PDF export, AI advisor (paid add-on), and analytics. Guests are limited to 3 saved loads per day; paid plans are unlimited."],
        ["Accounts", "You are responsible for maintaining the confidentiality of your credentials and all activity under your account."],
        ["Billing", "Subscriptions are billed monthly in advance via Lemon Squeezy. Prices are shown in USD and may change with 30-day notice for existing subscribers."],
        ["Acceptable use", "No scraping, reverse engineering, spamming feedback, or attempting to bypass the freemium limits. We reserve the right to suspend abusive accounts."],
        ["Warranty", "The service is provided 'as is' without warranty. Profitability calculations are informational and do not constitute financial advice."],
        ["Limitation of liability", "To the maximum extent permitted by law, our total liability is limited to fees you paid in the 12 months preceding the claim."],
        ["Governing law", "These terms are governed by Delaware law, USA."],
      ],
    },
    ar: {
      title: "شروط الخدمة",
      updated: "آخر تحديث: فبراير 2026",
      body: [
        ["القبول", "باستخدامك المنصة توافق على هذه الشروط. إن لم توافق، لا تستخدم الخدمة."],
        ["الخدمة", "نوفر حاسبة ربحية، سجل حمولات، تصدير PDF، مستشار ذكي (إضافة مدفوعة)، وتحليلات. الضيوف محدودون بـ 3 حمولات يوميًا، الخطط المدفوعة غير محدودة."],
        ["الحسابات", "أنت مسؤول عن سرية بيانات الدخول وكل نشاط ضمن حسابك."],
        ["الفوترة", "الاشتراكات شهرية مقدمًا عبر Lemon Squeezy. الأسعار بالدولار الأمريكي وقد تتغير بإشعار 30 يومًا للمشتركين الحاليين."],
        ["الاستخدام المقبول", "ممنوع الـ scraping أو الهندسة العكسية أو إساءة استخدام الحد المجاني. نحتفظ بحق تعليق الحسابات المسيئة."],
        ["الضمان", "الخدمة مقدمة كما هي بدون ضمان. حسابات الربحية معلوماتية وليست نصيحة مالية."],
        ["حد المسؤولية", "بالحد الأقصى المسموح قانونًا، مسؤوليتنا الكلية محدودة بالرسوم المدفوعة خلال 12 شهرًا السابقة."],
        ["القانون الحاكم", "قوانين ولاية ديلاوير الأمريكية."],
      ],
    },
  },
  refund: {
    en: {
      title: "Refund Policy",
      updated: "Last updated: February 2026",
      body: [
        ["7-day money-back guarantee", "New paid subscribers may request a full refund within 7 days of their first charge — no questions asked. Email support@loadwise.app from your account email."],
        ["Cancel anytime", "You may cancel your subscription at any time from your billing page or the Lemon Squeezy customer portal. Cancellation stops future renewals; the current billing period remains active until its end date."],
        ["Prorated refunds", "Beyond the 7-day window, subscription fees are non-refundable but you retain full access until the end of the paid period."],
        ["Failed payments", "If a renewal fails, we retry for 5 days. If payment is not received your subscription is suspended, not deleted — reactivate any time."],
        ["Chargebacks", "Please contact us first — we resolve billing issues within one business day, faster than any chargeback process."],
        ["Contact", "Refund requests: support@loadwise.app"],
      ],
    },
    ar: {
      title: "سياسة الاسترداد",
      updated: "آخر تحديث: فبراير 2026",
      body: [
        ["ضمان استرداد لمدة 7 أيام", "يمكن للمشتركين الجدد طلب استرداد كامل خلال 7 أيام من أول عملية شحن — بدون أسئلة. راسل support@loadwise.app من نفس بريد حسابك."],
        ["إلغاء في أي وقت", "يمكنك إلغاء اشتراكك في أي وقت من صفحة الفوترة أو Lemon Squeezy customer portal. الإلغاء يوقف التجديد التلقائي، وتبقى الخدمة نشطة حتى نهاية الفترة الحالية."],
        ["الاسترداد النسبي", "بعد نافذة الـ 7 أيام، رسوم الاشتراك غير قابلة للاسترداد، لكن تحتفظ بالوصول الكامل حتى نهاية الفترة المدفوعة."],
        ["فشل الدفع", "في حال فشل التجديد، نعيد المحاولة لمدة 5 أيام. إذا لم يُستَلَم الدفع يُعلَّق اشتراكك ولا يُحذف — يمكن إعادة تفعيله في أي وقت."],
        ["Chargebacks", "تواصل معنا أولاً — نحل مشاكل الفوترة خلال يوم عمل واحد، أسرع من أي عملية chargeback."],
        ["اتصل بنا", "طلبات الاسترداد: support@loadwise.app"],
      ],
    },
  },
};

export default function Legal({ page }) {
  const [lang, setLang] = useState(() => localStorage.getItem("dpc_lang") || "en");
  const c = CONTENT[page][lang];

  React.useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#0a192f] text-white landing-root">
      <div className="ticker-line" />
      <header className="max-w-[900px] mx-auto px-6 md:px-8 py-6 flex items-center justify-between">
        <Link to="/" className="text-sm text-slate-300 hover:text-white">← Home</Link>
        <button
          onClick={() => {
            const next = lang === "en" ? "ar" : "en";
            setLang(next);
            localStorage.setItem("dpc_lang", next);
          }}
          className="text-xs text-slate-300 hover:text-white px-3 py-2 rounded-lg border border-white/15"
        >
          {lang === "en" ? "عربي" : "English"}
        </button>
      </header>
      <main className="max-w-[900px] mx-auto px-6 md:px-8 pb-16">
        <div className="kicker"><span className="dot" />Legal</div>
        <h1 className="h-section mt-4 text-white">{c.title}</h1>
        <p className="mt-2 text-slate-500 text-sm">{c.updated}</p>
        <div className="mt-8 glass rounded-2xl p-6 md:p-8 space-y-6">
          {c.body.map(([h, p], i) => (
            <div key={i}>
              <h2 className="text-white text-lg font-semibold tracking-tight">{h}</h2>
              <p className="mt-2 text-slate-300 text-sm leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
